import "./synchronisemenu.scss";
import { type FC, type ReactNode, useState } from "react";
import { Icon } from "@onshopfront/components";
import { useShopfrontDispatch, useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import { showDialog } from "#/Actions/Dialogs/DialogActions.ts";
import { LAYOUT_BODY_CLICK } from "#/Actions/General/LayoutActions.ts";
import { ExportLoggingDialog } from "#/Common/Logging/ExportLoggingDialog.tsx";
import type { QueuedItem } from "#/Data/_Bases/BaseUploadRepository.ts";
import { determineDataSource } from "#/Data/DataStores/DataSource.ts";
import { usePermissions } from "#/Utilities/Domain/Users/Permissions.tsx";
import { fireEventType } from "#/Utilities/Generic/ActionsHelper.ts";
import { POS_FORCE_SYNC } from "#/Workers/Sync/SyncEvents.ts";
import { UPLOAD_RETRY_LIMIT, type UPLOAD_TYPES } from "#/Workers/Upload/UploadEvents.ts";
import Button, { OKOptionButton } from "../../../Common/Buttons/Button";
import ClearLocalDataDialog from "../../../Common/Dialogs/ClearLocalDataDialog";
import ClearUploadQueueDialog from "../../../Common/Dialogs/ClearUploadQueueDialog";
import PopupManager from "../../../Common/Popups/PopupManager";
import Request from "../../../Utilities/Platform/Request";
import { OfflineSaleBackup } from "./Backup/OfflineSaleBackup";
import { type SyncErrorsType, SynchroniseErrors } from "./SynchroniseErrors";
import ForceUploadDialog from "./Upload/ForceUploadDialog";

interface Props {
    embeddedDisabled: boolean;
    errors: Array<QueuedItem<UPLOAD_TYPES>>;
    onRefresh: () => void;
}

const permissions = [ "clear_local_data", "modify_settings" ] as const;

export const SynchroniseMenu: FC<Props> = ({
    embeddedDisabled,
    errors,
    onRefresh,
}) => {
    const open = useShopfrontSelector(state => state.layout.synchronise.open);
    const offline = useShopfrontSelector(state => !state.information.online);

    const [ reconnecting, setReconnecting ] = useState(false);

    const { clearLocalData, modifySettings } = usePermissions(permissions);

    const dispatch = useShopfrontDispatch();

    const onForceSync = (): void => {
        console.log("Force sync button pressed");
        dispatch(fireEventType(LAYOUT_BODY_CLICK));

        if(typeof document !== "undefined") {
            document.dispatchEvent(new CustomEvent(POS_FORCE_SYNC, {
                detail: {
                    full: true,
                },
            }));
        }
    };

    const onForceUpload = (): void => {
        dispatch(fireEventType(LAYOUT_BODY_CLICK));

        determineDataSource()
            .then(source => source.uploads.all())
            .then(uploads => {
                if(uploads.length) {
                    dispatch(showDialog(
                        "Run Upload Queue",
                        <ForceUploadDialog />,
                        "question"
                    ));
                } else {
                    PopupManager.info("Nothing queued to be uploaded");
                }
            });
    };

    const onClearLocal = async (): Promise<void> => {
        const source = await determineDataSource();

        const uploads = await source.uploads.all();

        dispatch(fireEventType(LAYOUT_BODY_CLICK));

        if(!uploads.length) {
            dispatch(showDialog(
                "Clear Local Data",
                <ClearLocalDataDialog />,
                "warning",
                false,
                false
            ));

            return;
        }

        const queue: {
            pending: Partial<Record<UPLOAD_TYPES, number>>;
            failed: Partial<Record<UPLOAD_TYPES, number>>;
        } = {
            pending: {},
            failed : {},
        };

        // Loop through upload queue and sort by status and type
        for(let i = 0; i < uploads.length; i++) {
            const { type, attempts } = uploads[i];

            if(!type || typeof attempts !== "number") {
                continue;
            }

            const list = (attempts < UPLOAD_RETRY_LIMIT) ? "pending" : "failed";

            if(!queue[list][type]) {
                queue[list][type] = 0;
            }

            (queue[list][type] as number)++;
        }

        dispatch(showDialog(
            "Clearing Uploads",
            <ClearUploadQueueDialog queue={queue} />,
            "warning",
            false,
            false
        ));
    };

    const onDismissErrors = (ids: Array<number>): void => {
        determineDataSource()
            .then(source => {
                return source.uploads.dismissErrors(ids);
            })
            .then(() => {
                onRefresh();
            });
    };

    const onReconnectClick = async (): Promise<void> => {
        setReconnecting(true);

        try {
            await Request.get(`/areyouonline?id=${Date.now() * (Math.random() + 0.1)}`);
        } catch {
            // Ignore
        }

        setReconnecting(false);
    };

    const onExportLogging = (): void => {
        dispatch(showDialog(
            "Export Logging",
            <ExportLoggingDialog />,
            "information",
            false,
            true
        ));
    };

    const onOfflineBackupClick = () => {
        if(typeof window.showDirectoryPicker === "undefined") {
            dispatch(showDialog(
                "Offline Backup Unsupported",
                <p className="max-w-[500px]">
                    Offline Sale Backup is not supported in your browser. Please make sure you&apos;re using the latest
                    version of Google Chrome
                </p>,
                "warning",
                [ OKOptionButton ]
            ));

            return;
        }

        dispatch(showDialog(
            "Offline Sales Backup",
            <OfflineSaleBackup />,
            "question",
            [ OKOptionButton ]
        ));
    };

    const classes: Array<string> = [];

    if(open) {
        classes.push("open");
    }

    let content: ReactNode;

    if(offline) {
        classes.push("offline");

        content = (
            <div className="offline-message">
                <div>You cannot access sync functions whilst offline</div>
                <Button classes={[ "button-save" ]} onClick={onReconnectClick} >
                    {
                        reconnecting ?
                            <Icon icon={[ "fal", "spinner-third" ]} spin /> :
                            "Reconnect"
                    }
                </Button>
                {modifySettings && (
                    <Button
                        classes={[ "button-sync" ]}
                        onClick={onOfflineBackupClick}
                    >
                        <Icon
                            icon={[ "fas", "cabinet-filing" ]}
                            className="mr-2"
                        />
                        Offline Sale Backup
                    </Button>
                )}
            </div>
        );
    } else {
        let uploads = null;

        if(errors.length || embeddedDisabled) {
            const errorMap: SyncErrorsType = {};

            // Count errors types
            for(const error of errors) {
                if(!error.type) {
                    continue;
                }

                if(Array.isArray(errorMap[error.type])) {
                    (errorMap[error.type] as Array<QueuedItem<UPLOAD_TYPES>>).push(error);
                } else {
                    errorMap[error.type] = [ error ];
                }
            }

            uploads = (
                <SynchroniseErrors
                    errors={errorMap}
                    embeddedDisabled={embeddedDisabled}
                    onDismiss={onDismissErrors}
                />
            );
        }

        content = (
            <div className="synchronise-menu-content">
                {uploads}
                <div className="synchronise-menu-actions">
                    <div className="synchronise-warning">
                        <div>
                            Please note, clearing local data or forcing
                            a synchronise will <strong>clear</strong> any outstanding sales and all other local data.
                        </div>
                        <div>Use these only if you know what you are doing</div>
                    </div>
                    <div className="synchronise-menu-buttons">
                        <Button
                            classes={[ "button-bug" ]}
                            onClick={onExportLogging}
                        >
                            <Icon
                                icon={[ "fas", "bug" ]}
                                className="mr-2"
                                fixedWidth
                            />
                            Export Logging
                        </Button>
                        {modifySettings && (
                            <Button
                                classes={[ "button-sync" ]}
                                onClick={onOfflineBackupClick}
                            >
                                <Icon
                                    icon={[ "fas", "cabinet-filing" ]}
                                    className="mr-2"
                                    fixedWidth
                                />
                                Offline Sale Backup
                            </Button>
                        )}
                        <Button
                            classes={[ "button-sync" ]}
                            onClick={onForceSync}
                        >
                            <Icon
                                icon={[ "fas", "sync" ]}
                                className="mr-2"
                                fixedWidth
                            />
                            Force Synchronisation
                        </Button>
                        <Button
                            classes={[ "button-sync" ]}
                            onClick={onForceUpload}
                        >
                            <Icon
                                icon={[ "fas", "upload" ]}
                                className="mr-2"
                                fixedWidth
                            />
                            Force Upload
                        </Button>
                        {clearLocalData && (
                            <Button
                                classes={[ "button-clear" ]}
                                onClick={onClearLocal}
                            >
                                <Icon
                                    icon={[ "fas", "trash" ]}
                                    className="mr-2"
                                    fixedWidth
                                />
                                Clear Local Data
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="synchronise-menu" className={classes.join(" ")}>
            {content}
        </div>
    );
};
